import { Validator } from "vee-validate";
import ImageFileCompression from "@/commons/utils/ImageFileCompression";
import { getType } from "mime";

/**
 * ### 他事業所連携メッセージ送信共通ビジネスロジック
 */
export default class OtherOfficeMessageFile {
  /** バリデーター */
  private validator: Validator;
  /** バリデーターで使用するフィールド名 */
  private fieldName: string;

  /**
   * ### コンストラクター
   *
   * @param validator バリデーション用クラス
   * @param fieldName バリデーションで使用するフィールド名
   */
  constructor(validator: Validator, fieldName: string) {
    this.validator = validator;
    this.fieldName = fieldName;
  }

  /**
   * ### 登録対象のメッセージ送信ファイルを作成する
   *
   * - PDFを優先ソートしてからファイルを読み込む
   * - エラーがあった場合、無効な操作として空の配列を返す
   *
   * @param files 登録対象候補のファイル
   */
  public async createAddFile(files: Array<File>, limit: number, currentFileCount: number): Promise<Array<File>> {
    // async処理でPromiseメソッドを実行しないと画面表示が崩れるので空のPromiseメソッドを実行する
    await this.emptyProsess();

    // 現在のバリデーションエラーをリセットしてから、バリデーションを実施する
    this.validator.errors.remove(this.fieldName);

    // 追加可能な残ファイル数を計算し、追加不可ならエラーメッセージを登録して即終了
    // 追加可能な残ファイル数は後続処理でも「あと何ファイル追加できるか」の計算で使用する
    let canAddFileCount: number = limit - currentFileCount;
    if (canAddFileCount < 1) {
      this.validator.errors.add({ field: this.fieldName, msg: `添付ファイル最大登録ファイル数以上は登録できません` });
      return [];
    }

    // 親コンポーネントに渡すファイルを作成する
    // PDFの場合は1ページを1枚のjpegに変換して、メッセージ送信1枚分として扱う
    const addFiles: Array<File> = [];

    for (const file of files) {
      // ファイルのメディアタイプチェック
      // APIと同じライブラリでチェックすることで整合性を担保している
      const mime: string | null = this.getType(file.name);
      if (mime == null || mime != file.type) {
        const errorMessage: string = `${file.name}はjpg、png、pdf、doc、docx、xls、xlsx、ppt、pptx以外の拡張子のため登録できません`;
        this.validator.errors.add({ field: this.fieldName, msg: errorMessage });
        return [];
      }

      // 追加可能かチェック
      if (canAddFileCount < 1) {
        this.validator.errors.add({ field: this.fieldName, msg: `添付ファイル最大登録ファイル数以上は登録できません` });
        return [];
      }

      switch (file.type) {
        case "application/pdf": {
          const valid: boolean = this.validationFileSize(50, file);
          if (valid) {
            addFiles.push(file);
            canAddFileCount--;
          }
          break;
        }
        case "image/jpeg":
        case "image/png": {
          try {
            const compressedFile: File | undefined = await this.compressImage(file);
            if (!compressedFile) {
              continue;
            }
            addFiles.push(compressedFile);
            canAddFileCount--;
          } catch (error) {
            const _error: Error = error as Error;
            this.validator.errors.add({ field: this.fieldName, msg: `指定されたファイルは${_error.message}` });
            return [];
          }
          break;
        }
        case "application/msword":
        case "application/vnd.openxmlformats-officedocument.wordprocessingml.document":
        case "application/vnd.ms-excel":
        case "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet":
        case "application/vnd.ms-powerpoint":
        case "application/vnd.openxmlformats-officedocument.presentationml.presentation": {
          const valid: boolean = this.validationFileSize(10, file);
          if (valid) {
            addFiles.push(file);
            canAddFileCount--;
          }
          break;
        }
        default: {
          const errorMessage: string = `${file.name}はjpg、png、pdf、doc、docx、xls、xlsx、ppt、pptx以外の拡張子のため登録できません`;
          this.validator.errors.add({ field: this.fieldName, msg: errorMessage });
          return [];
        }
      }
    }

    return addFiles;
  }

  public async emptyProsess(): Promise<void> {
    return new Promise((reject: Function) => {
      reject();
    });
  }

  /**
   * ## ファイルのパスまたは拡張子からContent-Typeを取得する
   */
  public getType(fileString: string): string | null {
    /**
     * getTypeの引数にはファイルパスが必要
     * 先頭文字列が「.」の場合、拡張子でありファイルパスではないため、先頭にファイル名を付けてファイルパスにする
     * 先頭文字列が「.」ではない場合、ファイルパスとする
     */
    let contentType: string | null = "";
    if (fileString[0] == ".") {
      contentType = getType(`file${fileString}`);
    } else {
      contentType = getType(`${fileString}`);
    }

    return contentType;
  }

  /**
   * ### 画像ファイルのサイズを圧縮する
   */
  public async compressImage(file: File): Promise<File | undefined> {
    const compressedFile: File | undefined = await ImageFileCompression.imageCompression(file, "image/jpeg", 1 * 1024 * 1024, 4096, 4096);
    return compressedFile ? compressedFile : undefined;
  }

  public validationFileSize(limitMb: number, file: File): boolean {
    const fileSize: number = 1024 * 1024 * limitMb;
    if (fileSize < file.size) {
      const errorMessage: string = `${file.name}のファイルサイズが${limitMb}MBを超えているため登録できません`;
      this.validator.errors.add({ field: this.fieldName, msg: errorMessage });
      return false;
    } else {
      return true;
    }
  }
}
